import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../api/baseUrl";
import { addPrescription, updatePrescription } from "../../../redux/diagnosestate/diagnoseAction";
import {
  validatePrescriptionForm,
  type PrescriptionFormData,
} from "./prescriptionFormUtils";
import { isInventoryEnabled } from "../../../utils/clinicFeatures";
import {
  FREQUENCY_OPTIONS,
  calculatePrescriptionQty,
  doseDisplayValue,
  doseOptionId,
  usesFormulaQty,
} from "./prescriptionDispenseUtils";

type FormData = PrescriptionFormData;

interface LinkedProductInfo {
  _id: string;
  productName?: string;
  scale?: string;
  availableQuantity?: number;
  price?: number;
  linkedDrugDoseId?: string;
}

interface PrescriptionEditItem {
  _id: string;
  drugsId?: string;
  drugs?: string;
  type?: string;
  dose?: string;
  doseId?: string;
  frequency?: string;
  duration?: string;
  qty?: number | string;
  dispense?: boolean;
  productId?: string;
  createdAt?: string;
}

interface PrescriptionFormProps {
  treatmentId?: string;
  editItem?: PrescriptionEditItem | null;
  onCancelEdit?: () => void;
  onSuccess?: (item: any) => void;
  useRedux?: boolean;
  localOnly?: boolean;
  deferSave?: boolean;
  draftId?: string;
  onDraftChange?: (draftId: string, formData: PrescriptionFormData) => void;
  onDraftRemove?: (draftId: string) => void;
  inlineRow?: boolean;
  /** Draft restored from the shell's store; seeds the form on mount. */
  initialDraft?: Partial<PrescriptionFormData>;
  /** Why the last Save All rejected this row. */
  draftError?: string;
}

function PrescriptionForm({
  treatmentId: treatmentIdProp,
  editItem,
  onCancelEdit,
  onSuccess,
  useRedux = true,
  localOnly = false,
  deferSave = false,
  draftId,
  onDraftChange,
  onDraftRemove,
  inlineRow = false,
  initialDraft,
  draftError,
}: PrescriptionFormProps) {
  const dispatch = useDispatch();
  // Provide means "the clinic hands this over from stock" — meaningless
  // without the Inventory module
  const inventoryEnabled = isInventoryEnabled();
  const { ID } = useParams();
  const treatmentId = treatmentIdProp || ID;
  const initialFormData: FormData = {
    drugsId: "",
    drugsIds: "",
    drugs: "",
    dose: "",
    doseId: "",
    doseIds: "",
    frequency: "",
    duration: "",
    type: "",
    qty: "",
    dispense: false,
    productId: "",
    availableStock: 0,
    productScale: "",
    unitPrice: 0,
    linkedProductName: "",
  };
  const [formData, setformData] = useState<FormData>(() =>
    deferSave && initialDraft && Object.keys(initialDraft).length > 0
      ? { ...initialFormData, ...initialDraft }
      : initialFormData
  );

  const [data, setData] = useState([]);
  const [linkedProducts, setLinkedProducts] = useState<LinkedProductInfo[]>([]);

  // Read-only inventory info for the doctor's badge and the qty formula.
  // It never gates or flips the Provide flag — Provide is a pure signal, and
  // product/price/stock are resolved live at the cashier.
  const applyLinkedProduct = useCallback(
    (
      prev: FormData,
      doseId: string,
      products: LinkedProductInfo[],
      opts?: { preserveQty?: boolean }
    ): FormData => {
      // A dose may be filled by several brands; the badge summarises them and
      // the qty formula uses their shared scale.
      const matches = products.filter(
        (p) => String(p.linkedDrugDoseId || "") === String(doseId)
      );
      const linked = matches[0];
      const stock = matches.reduce(
        (sum, p) => sum + (Number(p.availableQuantity) || 0),
        0
      );
      const scale = linked?.scale || "";
      const calcQty = calculatePrescriptionQty(
        prev.frequency,
        prev.duration,
        scale
      );
      return {
        ...prev,
        productId: linked?._id || "",
        availableStock: stock,
        productScale: scale,
        unitPrice: Number(linked?.price) || 0,
        linkedProductName: linked?.productName || "",
        linkedProductCount: matches.length,
        qty:
          opts?.preserveQty && prev.qty !== "" && prev.qty != null
            ? prev.qty
            : calcQty != null
              ? calcQty
              : prev.qty !== "" && prev.qty != null
                ? prev.qty
                : "",
      };
    },
    []
  );

  useEffect(() => {
    baseUrl
      .get("/client/prescription/prescription/findAllPrescription")
      .then((response) => {
        setData(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to fetch diagnoses. Please try again.");
      });

    if (!inventoryEnabled) return;
    baseUrl
      .get("/client/prescription/prescription/searchProductsForDrugLink")
      .then((response) => {
        setLinkedProducts(response.data.data || []);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [inventoryEnabled]);

  useEffect(() => {
    if (deferSave && draftId) return;
    if (!editItem) {
      setformData(initialFormData);
      return;
    }
    const doseId = editItem.doseId || editItem.dose || "";
    const doseLabel = editItem.dose || editItem.doseId || "";
    const base: FormData = {
      drugsId: editItem.drugsId || "",
      drugsIds: `${editItem.drugsId}/${editItem.drugs}/${editItem.type}`,
      drugs: editItem.drugs || "",
      dose: doseLabel,
      doseId,
      doseIds: `${doseId}/${doseLabel}`,
      frequency: editItem.frequency || "",
      duration: editItem.duration || "",
      type: editItem.type || "",
      qty: editItem.qty ?? "",
      dispense: Boolean(editItem.dispense),
      productId: editItem.productId || "",
      availableStock: 0,
      productScale: "",
      unitPrice: 0,
      linkedProductName: "",
    };
    setformData(
      applyLinkedProduct(base, doseId, linkedProducts, { preserveQty: true })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, deferSave, draftId, linkedProducts, applyLinkedProduct]);

  useEffect(() => {
    if (deferSave && draftId && onDraftChange) {
      onDraftChange(draftId, formData);
    }
  }, [deferSave, draftId, formData, onDraftChange]);

  // Recalculate qty when frequency/duration/scale change (formula UOMs only)
  useEffect(() => {
    if (!formData.frequency || !formData.duration) return;
    if (!usesFormulaQty(formData.productScale)) return;
    const calcQty = calculatePrescriptionQty(
      formData.frequency,
      formData.duration,
      formData.productScale
    );
    if (calcQty == null) return;
    setformData((prev) =>
      String(prev.qty) === String(calcQty) ? prev : { ...prev, qty: calcQty }
    );
  }, [
    formData.frequency,
    formData.duration,
    formData.productScale,
  ]);

  const diagnoseOptions = useMemo(
    () =>
      data.map((item: any) => (
        <option
          value={item._id + "/" + item.drugs + "/" + item.type}
          key={item._id}
        >
          {item.drugs}
        </option>
      )),
    [data]
  );
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === "drugsIds") {
      setformData((prev) => ({
        ...prev,
        [name]: value,
        drugsId: value.split("/")[0],
        drugs: value.split("/")[1],
        type: value.split("/")[2],
        dose: "",
        doseId: "",
        doseIds: "",
        qty: "",
        dispense: false,
        productId: "",
        availableStock: 0,
        productScale: "",
        unitPrice: 0,
        linkedProductName: "",
      }));
    } else if (name === "doseIds") {
      const doseId = value.split("/")[0];
      const dose = value.split("/").slice(1).join("/") || value.split("/")[1];
      setformData((prev) =>
        applyLinkedProduct(
          {
            ...prev,
            [name]: value,
            doseId,
            dose,
          },
          doseId,
          linkedProducts
        )
      );
    } else if (name === "dispense") {
      setformData((prev) => ({ ...prev, dispense: checked }));
    } else if (name === "frequency" || name === "duration") {
      setformData((prev) => {
        const next = { ...prev, [name]: value };
        if (usesFormulaQty(prev.productScale)) {
          const calcQty = calculatePrescriptionQty(
            name === "frequency" ? value : prev.frequency,
            name === "duration" ? value : prev.duration,
            prev.productScale
          );
          if (calcQty != null) next.qty = calcQty;
        }
        return next;
      });
    } else if (type === "checkbox") {
      setformData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setformData((prev) => ({ ...prev, [name]: value }));
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  const DURATION_OPTIONS = ["1", "2", "3", "4", "5", "7", "10", "14"];

  const compactSelectClass =
    "w-full min-w-0 px-2 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500";

  const submitPayload = (): PrescriptionFormData => ({
    ...formData,
    qty: formData.qty === "" || formData.qty == null ? undefined : formData.qty,
    dispense: Boolean(formData.dispense),
  });

  const handleSubmit = async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = submitPayload();
      const validation = validatePrescriptionForm(payload);
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }

      if (localOnly) {
        const normalizedItem = {
          ...payload,
          _id: editItem?._id || `local-${Date.now()}`,
          createdAt: editItem?.createdAt || new Date().toISOString(),
          drugs: payload.drugs,
          dose: payload.dose || payload.doseId,
        };
        onSuccess?.(normalizedItem);
        onCancelEdit?.();
        setformData(initialFormData);
        toast.success(editItem ? "Prescription updated." : "Prescription added.");
        return;
      }

      if (editItem?._id) {
        const response = await baseUrl.patch(
          "/client/treatments/prescription/updatePrescription",
          payload,
          {
            headers: {
              treatmentid: treatmentId,
              prescriptionid: editItem._id,
            },
          }
        );
        const updated = response.data.data;
        const normalizedItem = {
          ...payload,
          _id: editItem._id,
          createdAt: updated?.createdAt || new Date().toISOString(),
          drugs: payload.drugs,
          dose: payload.dose || payload.doseId,
        };
        if (useRedux) {
          dispatch(updatePrescription(normalizedItem));
        }
        onSuccess?.(normalizedItem);
        onCancelEdit?.();
        setformData(initialFormData);
        toast.success("Prescription updated.");
        return;
      }

      const response = await baseUrl.patch(
        "/client/treatments/prescription/addprescription",
        payload,
        {
          headers: { treatmentid: treatmentId },
        }
      );

      const normalizedItem = {
        ...payload,
        createdAt: response.data.data.createdAt,
        _id: response.data.data._id,
        drugs: payload.drugs,
        dose: payload.dose || payload.doseId,
      };

      if (useRedux) {
        dispatch(addPrescription(normalizedItem));
      }
      onSuccess?.(normalizedItem);
      setformData(initialFormData);
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          (editItem
            ? "Failed to update prescription."
            : "Failed to submit the form.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRowSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    void handleSubmit();
  };

  const doseOptions = data
    ?.filter((item: any) => item._id === formData.drugsId)
    ?.map((filteredItem: any) => filteredItem.dose)
    ?.flat();

  const qtyDispenseFields = (
    <>
      <div>
        <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">
          Qty
        </label>
        <input
          type="number"
          name="qty"
          min={0}
          value={formData.qty ?? ""}
          onChange={handleChange}
          className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          placeholder="Qty"
        />
        {inventoryEnabled &&
          formData.doseId &&
          (formData.linkedProductName ? (
            <p className="mt-1 text-[10px] text-teal-600 dark:text-teal-400 truncate">
              ✓ In inventory ·{" "}
              {Number(formData.linkedProductCount) > 1
                ? `${formData.linkedProductCount} brands`
                : formData.linkedProductName}{" "}
              · stock {formData.availableStock}
            </p>
          ) : (
            <p className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
              Not in inventory
            </p>
          ))}
      </div>
      {inventoryEnabled && (
        <div className="flex items-end pb-1">
          <label
            className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer"
            title="Recommend the clinic provides this drug — checked against live stock at billing"
          >
            <input
              type="checkbox"
              name="dispense"
              checked={Boolean(formData.dispense)}
              onChange={handleChange}
              className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            Provide
          </label>
        </div>
      )}
    </>
  );

  const fields = (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {/* Drugs */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Drugs *</label>
          <select
            name="drugsIds"
            value={formData.drugsIds}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Drug</option>
            {diagnoseOptions}
          </select>
        </div>

        {/* Dose */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Dose *</label>
          <select
            name="doseIds"
            value={formData.doseIds}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Dose</option>
            {doseOptions?.map((item: any, i: number) => {
              const id = doseOptionId(item);
              const label = doseDisplayValue(item);
              return (
                <option key={id || i} value={id + "/" + label}>
                  {label}
                  {formData.type}
                </option>
              );
            })}
          </select>
        </div>

        {/* Frequency */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Frequency *</label>
          <select
            name="frequency"
            value={formData.frequency}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Frequency</option>
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-400 mb-1">Duration (Days) *</label>
          <select
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-600 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
          >
            <option value="">Select Duration</option>
            {DURATION_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {days} Day{days === "1" ? "" : "s"}
              </option>
            ))}
          </select>
        </div>

        {qtyDispenseFields}
      </div>
  );

  if (inlineRow) {
    const isEdit = Boolean(editItem?._id);
    return (
      <tr className="bg-teal-50/60 dark:bg-teal-900/20 align-top">
        <td className="px-3 py-2" />
        <td className="px-3 py-2 min-w-[140px]">
          <select
            name="drugsIds"
            value={formData.drugsIds}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Drug *</option>
            {diagnoseOptions}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[100px]">
          <select
            name="doseIds"
            value={formData.doseIds}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Dose *</option>
            {doseOptions?.map((item: any, i: number) => {
              const id = doseOptionId(item);
              const label = doseDisplayValue(item);
              return (
                <option key={id || i} value={id + "/" + label}>
                  {label}
                  {formData.type}
                </option>
              );
            })}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[110px]">
          <select
            name="frequency"
            value={formData.frequency}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Freq *</option>
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[90px]">
          <select
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            className={compactSelectClass}
          >
            <option value="">Days *</option>
            {DURATION_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {days}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2 min-w-[70px]">
          <input
            type="number"
            name="qty"
            min={0}
            value={formData.qty ?? ""}
            onChange={handleChange}
            placeholder="Qty"
            className={compactSelectClass}
          />
        </td>
        <td className="px-3 py-2">
          {inventoryEnabled ? (
            <label
              className="inline-flex items-center gap-1 text-[10px] text-slate-600 dark:text-slate-300"
              title="Recommend the clinic provides this drug — checked against live stock at billing"
            >
              <input
                type="checkbox"
                name="dispense"
                checked={Boolean(formData.dispense)}
                onChange={handleChange}
                className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              Provide
            </label>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-slate-400">—</td>
        <td className="px-3 py-2">
          <div className="flex flex-col gap-1">
            {deferSave && !isEdit && (
              <span
                title={draftError ? `Not saved: ${draftError}` : undefined}
                className={`px-2 py-1 text-[10px] rounded-md font-medium whitespace-nowrap text-center ${
                  draftError
                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                    : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                }`}
              >
                {draftError ? "Not saved" : "Draft"}
              </span>
            )}
            {isEdit && !deferSave && (
              <button
                type="button"
                onClick={handleRowSubmit}
                disabled={isSubmitting}
                className="px-2 py-1 text-[10px] rounded-md bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium whitespace-nowrap disabled:opacity-50"
              >
                {isSubmitting ? "..." : "Update"}
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                deferSave && draftId
                  ? onDraftRemove?.(draftId)
                  : onCancelEdit?.()
              }
              className="px-2 py-1 text-[10px] rounded-md border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 whitespace-nowrap"
            >
              {deferSave && !isEdit ? "Remove" : "Cancel"}
            </button>
          </div>
        </td>
      </tr>
    );
  }

  if (deferSave && draftId && !inlineRow) {
    return (
      <div className="rounded-lg border border-dashed border-teal-300 dark:border-teal-700 bg-teal-50/40 dark:bg-teal-900/10 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
            Draft
          </span>
          <button
            type="button"
            onClick={() => onDraftRemove?.(draftId)}
            className="px-3 py-1 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            Remove
          </button>
        </div>
        <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
          {fields}
        </form>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {fields}

      <div className="flex justify-end gap-2">
        {editItem && onCancelEdit && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-medium text-xs hover:shadow-lg hover:shadow-teal-200 dark:hover:shadow-teal-900/30 transition-all disabled:opacity-50"
        >
          {isSubmitting
            ? editItem
              ? "Updating..."
              : "Adding..."
            : editItem
              ? "Update Prescription"
              : "Add Prescription"}
        </button>
      </div>
    </form>
  );
}

export default PrescriptionForm;
